import React from 'react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import CardFooter from 'components/Card/CardFooter';
import Button from 'components/CustomButtons/Button';
import CanPerform from 'components/CanPerform/CanPerform';
import LocalPhoneOutlined from '@material-ui/icons/LocalPhoneOutlined';
import SupervisorAccountOutlined from '@material-ui/icons/SupervisorAccountOutlined';
import AssignmentInd from '@material-ui/icons/AssignmentIndOutlined';
import LocationCity from '@material-ui/icons/LocationCity';
import Add from '@material-ui/icons/Add';
import HighlightOff from '@material-ui/icons/HighlightOff';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/departmentCardStyle';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { translate } from 'components/Translation';
import { BodyS, BodySBold, Caption, DisplayS, HeadingSBold } from 'components/Typography/Typography';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IDepartmentCardProps {
	department: string;
	uiColor: string;
	details?: {
		numbers: number;
		users: number;
	};
	managers: string[];
	addNumbers: () => void;
	onClick: (event?: Event) => void;
	notAssignedCard: boolean;
	disabled: boolean;
}

const DepartmentCard = ({
	department,
	uiColor,
	details,
	managers,
	addNumbers,
	notAssignedCard,
	disabled,
	...rest
}: IDepartmentCardProps) => {
	const classes = useStyles();
	const cardClasses = classNames({
		[classes.card]: true,
		[classes.cardHover]: !disabled,
		[classes.cardDisabled]: disabled,
	});
	const atLeastOneNumberOrUser: boolean = !!details && (details.users > 0 || details.numbers > 0);
	const noManagers: boolean = !managers || managers.length === 0;

	return (
		<Card data-test-id={`department-${department}`} className={cardClasses} {...rest}>
			<CardHeader icon className={classes.header}>
				<>
					<CardIcon uiColor={uiColor} size="sm">
						{notAssignedCard ? <HighlightOff /> : <LocationCity />}
					</CardIcon>
					<HeadingSBold color="primary-light">{department}</HeadingSBold>
				</>
			</CardHeader>
			<CardBody>
				<ConditionalRender
					show={atLeastOneNumberOrUser}
					onTrue={() => (
						<GridContainer>
							<GridItem xs={6} container justify="center" className={classes.numbers}>
								<LocalPhoneOutlined />
								<DisplayS color="primary-light">{String(details!.numbers)}</DisplayS>
							</GridItem>
							<GridItem xs={6} container justify="center" className={classes.numbers}>
								<SupervisorAccountOutlined />
								<DisplayS color="primary-light">{String(details!.users)}</DisplayS>
							</GridItem>
							<GridItem xs={6} container justify="center">
								<BodySBold color="disabled-light">{translate('numbers')}</BodySBold>
							</GridItem>
							<GridItem xs={6} container justify="center">
								<BodySBold color="disabled-light">{translate('users')}</BodySBold>
							</GridItem>
						</GridContainer>
					)}
					onFalse={() => (
						<GridContainer className={classes.addNumbersContainer}>
							<GridItem
								xs={12}
								className={classes.addNumbers}
								container
								justify="center"
								alignItems="center"
							>
								<CanPerform
									action="departments:add-numbers"
									yes={
										<Button
											color="tertiary-light"
											withIcon
											onClick={(event: any) => {
												event.stopPropagation();
												addNumbers();
											}}
										>
											<Add />
											<>{translate('add.numbers')}</>
										</Button>
									}
									no={
										<Caption color="secondary-light">
											{translate('departments.no.numbers.or.users.assigned')}
										</Caption>
									}
								/>
							</GridItem>
						</GridContainer>
					)}
				/>
			</CardBody>
			<ConditionalRender show={!notAssignedCard}>
				<CardFooter className={classes.cardFooter}>
					<GridContainer justify="flex-start" wrap="nowrap">
						<GridItem>
							<AssignmentInd />
						</GridItem>
						<GridItem>
							<BodyS color="primary-light">{translate('managers')}</BodyS>
							<div>
								{managers.map((manager, index) => (
									<BodyS color="disabled-light" key={index}>
										{`${manager}${index < managers.length - 1 ? ', ' : ''}`}
									</BodyS>
								))}
								<ConditionalRender
									show={noManagers}
									onTrue={() => (
										<BodyS color="disabled-light">{translate('departments.no.managers')}</BodyS>
									)}
								/>
							</div>
						</GridItem>
					</GridContainer>
				</CardFooter>
			</ConditionalRender>
		</Card>
	);
};

export default DepartmentCard;
